import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { Loader2, Plus, Search } from 'lucide-react';
import toast from 'react-hot-toast';

// --- LAYOUT ---
// ✅ Nueva navegación: TopbarMenu integra topbar + menú principal arriba.
// Sidebar y Topbar legacy quedan en /components por si necesitamos rollback.
import TopbarMenu from '../components/TopbarMenu';
import ModuleOnboardingPrompt from '../components/ModuleOnboardingPrompt';
import ReadOnlyBanner from '../components/ReadOnlyBanner';

// ✅ IA Asistente (embebido dentro del módulo, NO global flotante)
import Chatbot from './Chatbot';

// --- DASHBOARD ---
import Dashboard from './Dashboard';

// --- VENTAS ---
import VentasPOS from './VentasPOS';
import VentasHistorial from './VentasHistorial';
import VentasProfesional from './VentasProfesional';
import VentaDetalle from './VentaDetalle';
import Facturacion from './Facturacion';
import Presupuestos from './Presupuestos'; // <--- NUEVO IMPORT

// ✅ LISTAS DE PRECIOS (Importado)
import ListasPrecios from './ListasPrecios';
import ListasPreciosForm from './ListasPreciosForm';

// --- CLIENTES ---
import Clientes from './Clientes';
import ClientesForm from './ClientesForm';
import ClientesDetalle from './ClientesDetalle';
import ClientesMovimiento from './ClientesMovimiento';

// --- PRODUCTOS / INVENTARIO ---
import Productos from './Productos';
import ProductoNuevo from './ProductoNuevo';
// import ProductoEditar from './ProductoEditar'; // ❌ YA NO SE USA, LO REEMPLAZA ProductoNuevo
import ProductoDetalle from './ProductoDetalle';
import Inventario from './Inventario';
import InventarioAjuste from './InventarioAjuste';
import InventarioAjusteMasivo from './InventarioAjusteMasivo';
import InventarioTransferencia from './InventarioTransferencia';
import InventarioEtiquetas from './InventarioEtiquetas'; // ✅ NUEVO: Módulo de Etiquetas
import InventarioMovimientos from './InventarioMovimientos';

// ✅ NUEVO: COMBOS
import Combos from './Combos';
import ComboBuilder from './ComboBuilder';

// --- COMPRAS / PROVEEDORES ---
import Compras from './Compras';
import ComprasNueva from './ComprasNueva';
import ComprasDetalle from './ComprasDetalle';
import PedidoSugerido from './PedidoSugerido';
import Proveedores from './Proveedores';
import ProveedoresForm from './ProveedoresForm';
import ProveedoresDetalle from './ProveedoresDetalle';
import ProveedoresMovimiento from './ProveedoresMovimiento';

// ✅ NUEVO: COMPRAS OCR (Menú Adicional IA)
import ComprasOCR from './ComprasOCR';

// --- TESORERÍA / CAJA ---
import Caja from './Caja';
import CajaDetalle from './CajaDetalle';
import Bancos from './Bancos';
import BancosForm from './BancosForm';
import BancosMovimiento from './BancosMovimiento';
import Gastos from './Gastos';
import GastosForm from './GastosForm';

// --- TESORERÍA (OPERACIÓN) ---
import TesoreriaCobranzas from './TesoreriaCobranzas';
import TesoreriaPagos from './TesoreriaPagos';
import TesoreriaTransferencia from './TesoreriaTransferencia';
import { desactivarAvisosAdminEsteDispositivo } from '../utils/pushSubscription';
import ConciliacionBancaria from './ConciliacionBancaria';
import ConciliacionCuentasCorrientes from './ConciliacionCuentasCorrientes';

// --- REPORTES IMPOSITIVOS Y FINANCIEROS ---
import ReportesImpositivos from './ReportesImpositivos';
import ReportesCuentasCorrientes from './ReportesCuentasCorrientes';
import Reportes from './Reportes';
import ReportesInteligentes from './ReportesInteligentes';

// --- CONFIGURACIÓN ---
import EmpresaDatos from './EmpresaDatos';
import Sitios from './Sitios';
import Categorias from './Categorias';
import UnidadesMedida from './UnidadesMedida';
import CentrosCosto from './CentrosCosto';

// ✅ PANELES DE CONFIGURACIÓN (NUEVOS)
import ConfigProductosStock from './ConfigProductosStock';
import ConfigVentas from './ConfigVentas';
import ConfigFinanzas from './ConfigFinanzas';
import ConfigSistema from './ConfigSistema';

// 👉 NUEVOS (ventas y cobros)
import MetodosCobro from './MetodosCobro';
import MetodosCobroForm from './MetodosCobroForm';
import CondicionesPago from './CondicionesPago';
import TiposComprobante from './TiposComprobante';
import FlujoFondos from './FlujoFondos';
import FlujoFondosCalendarioPage from './FlujoFondosCalendarioPage';
import FlujoFondosAnalisisPage from './FlujoFondosAnalisisPage';
import FlujoFondosHistorialPage from './FlujoFondosHistorialPage';
import FlujoFondosBancosPage from './FlujoFondosBancosPage';
import FlujoFondosMesDetalle from './FlujoFondosMesDetalle';
import GestionCostos from './GestionCostos';
import GestionCostosActualizaciones from './GestionCostosActualizaciones';
import GestionCostosImportar from './GestionCostosImportar';

// ✅ NUEVO: Rentabilidad real
import RentabilidadVentas from './RentabilidadVentas';

import ConfigFacturacion from './ConfigFacturacion';
// ✅ NUEVO: Configuración de Reportes (Tickets, A4, etc.)
import ConfigReportes from './ConfigReportes';

import PresupuestosConfig from './PresupuestosConfig';

// ✅ NUEVO: IMPORTACIONES (para que exista en Router + Sidebar)
import Importaciones from './Importaciones';
import Prospectos from './Prospectos';

// --- SEGURIDAD ---
import Permisos from './Permisos';
import UsuarioNuevo from './UsuarioNuevo';
import UsuarioDetalle from './UsuarioDetalle';
import RolesConfig from './RolesConfig';
import SuperAdminEmpresas from './SuperAdminEmpresas';
import DemoRequests from './DemoRequests';
// Comprobantes manuales eliminados — cobro 100% Mercado Pago / AstroPay.
import PricingConfig from './superadmin/PricingConfig';
import SubscriptionsDashboard from './superadmin/SubscriptionsDashboard';
import SuperAdminPresets from './superadmin/SuperAdminPresets';
import SentryDashboard from './superadmin/SentryDashboard';
import Adopcion from './superadmin/Adopcion';
import AuditoriaSesiones from './AuditoriaSesiones';
import { CRM_ORG_MODULE_ALIAS_MAP } from '../utils/accessModules';
import {
  fetchOrganizationOnboardingStatus,
  shouldShowFirstRunOnboarding,
  isOnboardingV2Enabled,
  resumePendingProvisioning,
} from '../utils/onboarding';
import {
  completeModuleOnboardingStep,
  updateModuleOnboardingStatuses,
  getModuleOnboardingDefinition,
  normalizeModuleOnboardingStatuses,
  shouldShowModuleOnboarding,
  getModuleOnboardingStatus,
  MODULE_ONBOARDING_STATUS_COMPLETED,
  MODULE_ONBOARDING_STATUS_PENDING,
} from '../utils/moduleOnboarding';
import { fetchEffectiveModulePermissions } from '../utils/userModulePermissions';
import {
  buildFirstSaleOnboardingDefinition,
  completeFirstSaleOnboarding,
  FIRST_SALE_ONBOARDING_EVENT,
  FIRST_SALE_ONBOARDING_STEPS,
  FIRST_SALE_ONBOARDING_STORAGE_KEY,
  getFirstSaleOnboardingRoute,
  goToFirstSaleOnboardingStep,
  matchesFirstSaleOnboardingIdentity,
  readFirstSaleOnboarding,
  pauseFirstSaleOnboarding,
  hydrateFirstSaleOnboarding,
} from '../utils/firstSaleOnboarding';

// --- OTROS ---
import Agenda from './Agenda';
import AgendaDetalle from './AgendaDetalle';
import AgendaEditar from './AgendaEditar';
import AgendaNueva from './AgendaNueva';
import Logs from './Logs';
import ProgresoOnboarding from './ProgresoOnboarding';
import {
  fetchSetupOnboardingSignals,
  fetchSetupOnboardingState,
  buildSetupOnboardingChecklist,
  getSetupOnboardingProgress,
  completeSetupStepForTour
} from '../utils/setupOnboarding';
import {
  readSetupTour,
  cancelSetupTour,
  setupTourMatchesRoute,
  SETUP_TOURS
} from '../utils/setupOnboardingTours';
import { logNavigation } from '../utils/sessionAudit';

// ✅ CANAL DIGITAL (backoffice)
import Notificaciones from './Notificaciones';

// ✅ ACADEMIA
import AcademiaImpulza from './AcademiaImpulza';
import AcademiaViewer from './academia/AcademiaViewer';

const areStringArraysEqual = (left, right) => {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }

  return true;
};

const arePlainObjectsEqual = (left, right) => {
  if (left === right) return true;
  if (!left || !right) return false;

  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;

  for (const key of leftKeys) {
    if (left[key] !== right[key]) return false;
  }

  return true;
};
import PedidoDetalle from './PedidoDetalle';

// ✅ NUEVOS MÓDULOS ADMIN
import TiendaConfig from './TiendaConfig';
import IntegracionMercadoLibre from './IntegracionMercadoLibre';
import IntegracionRappi from './IntegracionRappi';
import ConfigTallesColores from './ConfigTallesColores';
import PedidosWeb from './PedidosWeb';
import Logistica from './Logistica';
import LogisticaEntregas from './LogisticaEntregas';
import LogisticaOperativa from './LogisticaOperativa';
import Devoluciones from './Devoluciones';
import DevolucionNueva from './DevolucionNueva';
import DevolucionDetalle from './DevolucionDetalle';
import ConfigLogistica from './ConfigLogistica';
import TiendaWeb from './Tienda';
import Rastreo from './Rastreo';

/* ======================================================
   ✅ PRODUCCIÓN (AGREGADO SIN BORRAR NADA)
   - Permite entrar a: Recetas, Registrar Producción, Historial, Distribución de costos
====================================================== */
import ProduccionRegistrar from './ProduccionRegistrar';
import ProduccionMasiva from './ProduccionMasiva';
import PedidosProduccion from './PedidosProduccion';
import ProduccionDetalle from './ProduccionDetalle';
import ProduccionFlujos from './ProduccionFlujos';
import Recetas from './Recetas';
import ProduccionHistorial from './ProduccionHistorial';
import ProduccionHistorialDetalle from './ProduccionHistorialDetalle';
import ProduccionDistribucion from './ProduccionDistribucion';
import { cargarTourDismissals, leerTourDismissalsCache, marcarTourCerrado, tourEstaCerrado } from '../utils/tourDismissals';

/**
 * Placeholder con layout estilo "Productos"
 */
const ModuloPlaceholder = ({ title = 'Módulo', actionLabel = 'Nueva acción', itemsLabel = 'Items' }) => {
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#10B981]">GASTIFY</div>
          <h1 className="text-2xl font-black text-[var(--text-default)] mt-1">{title}</h1>
          <div className="text-sm text-[var(--text-muted)] mt-1">
            Vista en preparación (placeholder). La ruta ya funciona, ahora el equipo no se tropieza con la puerta.
          </div>
        </div>

        <button className="flex items-center gap-2 px-4 py-3 rounded-md bg-[#10B981] text-white font-bold hover:bg-[#059669] transition-all">
          <Plus size={18} />
          <span className="text-[11px] font-black uppercase tracking-widest">{actionLabel}</span>
        </button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div className="rounded-md bg-[var(--surface)] border border-[color:var(--border)] p-4 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-muted)]">KPI</div>
          <div className="text-xl font-black text-[var(--text-default)] mt-2">0</div>
          <div className="text-xs text-[var(--text-muted)] mt-1">Pendiente de instrumentación</div>
        </div>
        <div className="rounded-md bg-[var(--surface)] border border-[color:var(--border)] p-4 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-muted)]">KPI</div>
          <div className="text-xl font-black text-[var(--text-default)] mt-2">0</div>
          <div className="text-xs text-[var(--text-muted)] mt-1">Pendiente de instrumentación</div>
        </div>
        <div className="rounded-md bg-[var(--surface)] border border-[color:var(--border)] p-4 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-muted)]">KPI</div>
          <div className="text-xl font-black text-[var(--text-default)] mt-2">0</div>
          <div className="text-xs text-[var(--text-muted)] mt-1">Pendiente de instrumentación</div>
        </div>
      </div>

      {/* Search + List */}
      <div className="rounded-md bg-[var(--surface)] border border-[color:var(--border)] shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 w-full max-w-lg bg-[var(--surface-2)] border border-[color:var(--border)] rounded-md px-3 py-2">
            <Search size={16} className="text-[var(--text-muted)]" />
            <input
              className="bg-transparent outline-none w-full text-sm"
              placeholder={`Buscar ${itemsLabel.toLowerCase()}...`}
              onChange={() => { }}
            />
          </div>
          <div className="text-xs text-[var(--text-muted)] whitespace-nowrap">0 resultados</div>
        </div>

        <div className="p-4">
          <div className="text-sm text-[var(--text-muted)]">
            Acá va el listado de {itemsLabel}. Por ahora, dejamos la estructura armada para que el “go-live” no dependa
            de milagros (ni de café).
          </div>

          <div className="mt-4 border border-[color:var(--border)] rounded-md overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[var(--surface-2)] text-[10px] font-black uppercase tracking-widest text-[var(--text-muted)]">
              <div className="col-span-4">Nombre</div>
              <div className="col-span-3">Estado</div>
              <div className="col-span-3">Actualizado</div>
              <div className="col-span-2 text-right">Acciones</div>
            </div>
            <div className="px-4 py-4 text-sm text-[var(--text-muted)]">
              Sin datos para mostrar (placeholder).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ROUTE_SCROLL_STORAGE_KEY = 'impulza:route-scroll:v1';

const getRouteScrollStorageKey = (location) =>
  `${ROUTE_SCROLL_STORAGE_KEY}:${location.pathname}${location.search}`;

const ProtectedRouteFallback = ({ title, description, onGoHome }) => (
  <div className="flex flex-col items-center justify-center h-full p-10 text-center">
    <h2 className="text-2xl font-bold">{title}</h2>
    {description ? <p className="mt-2 text-sm text-[var(--text-muted)]">{description}</p> : null}
    <button onClick={onGoHome} className="mt-6 bg-mint-500 text-white px-6 py-3 rounded-xl">
      Volver al inicio
    </button>
  </div>
);

const ProtectedRoute = ({ moduleKey, children, hasModuleAccess, hasPermission, onGoHome }) => {
  if (!hasModuleAccess(moduleKey)) {
    return (
      <ProtectedRouteFallback
        title="Modulo desactivado"
        description="Este modulo no esta habilitado para la organizacion actual."
        onGoHome={onGoHome}
      />
    );
  }
  if (!hasPermission(moduleKey)) {
    return <ProtectedRouteFallback title="Sin acceso" onGoHome={onGoHome} />;
  }
  return children;
};

const Sistema = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();




  const [activeModule, setActiveModule] = useState('dashboard');

  // Permisos (Roles)
  const [permisos, setPermisos] = useState([]);
  const [loadingPermisos, setLoadingPermisos] = useState(true);

  // ✅ NUEVO: Módulos Habilitados (Empresa/Plan)
  // Se obtiene de la tabla organizations.custom_modules
  const [orgModules, setOrgModules] = useState(null);
  const [organizationOnboardingStatus, setOrganizationOnboardingStatus] = useState('completed');
  const [moduleOnboardingStatuses, setModuleOnboardingStatuses] = useState({});
  // Gate de cohorte del onboarding v2 (F1/Fase 1). Solo true en altas nuevas.
  const [onboardingV2Enabled, setOnboardingV2Enabled] = useState(false);
  const [moduleOnboardingSaving, setModuleOnboardingSaving] = useState(false);
  const [dismissedModuleOnboarding, setDismissedModuleOnboarding] = useState(null);
  // Cierres de tours persistidos POR USUARIO (tabla onboarding_progress).
  // `dismissedModuleOnboarding` de arriba sigue existiendo para el cierre
  // dentro de la sesion; esto es lo que hace que el tour no vuelva a arrancar
  // de cero en el proximo login. Arranca del cache local para que el primer
  // render no muestre el cartel un instante antes de la respuesta de la base.
  const [tourDismissals, setTourDismissals] = useState(() => leerTourDismissalsCache(null));
  const [firstSaleOnboardingState, setFirstSaleOnboardingState] = useState(() => readFirstSaleOnboarding());
  const [dismissedFirstSaleOnboarding, setDismissedFirstSaleOnboarding] = useState(null);
  const mainScrollRef = useRef(null);

  // ✅ FIX: existía referencia a sitioSeleccionado?.id sin estar definido
  const [sitioSeleccionado, setSitioSeleccionado] = useState(null);

  // ── Setup Onboarding ("Empeza a operar") ──
  const [setupChecklist, setSetupChecklist] = useState([]);
  const [setupState, setSetupState] = useState(null);

  const loadSetupProgress = useCallback(async () => {
    const orgId = profile?.organization_id;
    if (!orgId) return;
    try {
      const [signals, state, orgRow] = await Promise.all([
        fetchSetupOnboardingSignals(orgId),
        fetchSetupOnboardingState(orgId),
        supabase
          .from('organizations')
          .select('onboarding_v2_enabled, business_type')
          .eq('id', orgId)
          .maybeSingle()
          .then(({ data }) => data || {})
      ]);
      const v2 = orgRow?.onboarding_v2_enabled === true;
      const bizType = v2 ? (orgRow?.business_type || null) : null;
      const checklist = buildSetupOnboardingChecklist(signals, state, bizType);
      setSetupChecklist(checklist);
      setSetupState(state);
    } catch (err) {
      console.warn('[Sistema] Error loading setup progress:', err);
    }
  }, [profile?.organization_id]);

  useEffect(() => {
    loadSetupProgress();

    const handleSetupChange = () => {
      loadSetupProgress();
    };
    window.addEventListener('impulza:setup-onboarding-change', handleSetupChange);
    return () => {
      window.removeEventListener('impulza:setup-onboarding-change', handleSetupChange);
    };
  }, [loadSetupProgress, location.pathname]);

  const setupProgress = useMemo(() => getSetupOnboardingProgress(setupChecklist), [setupChecklist]);
  // El acceso a /sistema/progreso NO se esconde al llegar al 100%: ahi viven
  // las tareas opcionales y las guias para repetir. Al terminar lo obligatorio
  // el boton cambia de tono (medalla) en vez de desaparecer.
  // (Antes leia `setupProgress.allDone`, propiedad que getSetupOnboardingProgress
  //  no devuelve —es `allRequiredDone`—, asi que la condicion nunca aplicaba.)
  const showSetupProgress = useMemo(() => setupChecklist.length > 0, [setupChecklist]);
  const setupProgressDone = useMemo(
    () => setupProgress.allRequiredDone === true,
    [setupProgress],
  );

  const [setupTourState, setSetupTourState] = useState(() => readSetupTour());

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncSetupTour = () => {
      setSetupTourState(readSetupTour());
    };

    window.addEventListener('impulza:setup-tour-change', syncSetupTour);
    window.addEventListener('impulza:onboarding-change', syncSetupTour);
    window.addEventListener('storage', syncSetupTour);
    return () => {
      window.removeEventListener('impulza:setup-tour-change', syncSetupTour);
      window.removeEventListener('impulza:onboarding-change', syncSetupTour);
      window.removeEventListener('storage', syncSetupTour);
    };
  }, []);

  // Terminar una guia da por hecho SU paso del checklist. Sin esto, un paso
  // solo se completaba si alguna señal de datos lo delataba, y esas señales se
  // pisan entre si (cargar un producto con costo marcaba tres pasos a la vez).
  const handleSetupTourCompleted = useCallback(() => {
    const tourId = setupTourState?.stepId || readSetupTour()?.stepId;
    cancelSetupTour();
    if (!tourId || !profile?.organization_id) return;
    completeSetupStepForTour({ organizationId: profile.organization_id, tourId })
      .catch((err) => console.error('[Sistema] No se pudo marcar el paso de la guia:', err));
  }, [setupTourState?.stepId, profile?.organization_id]);

  const activeSetupTourDefinition = useMemo(() => {
    const currentTour = readSetupTour() || setupTourState;
    if (!currentTour) return null;
    const definition = SETUP_TOURS[currentTour.stepId] || null;
    if (!definition) return null;
    // El tour queda guardado (reaparece al volver a su pantalla), pero solo se
    // renderiza donde viven sus selectores: fuera de contexto el cartel no puede
    // resaltar nada y tapa el recorrido guiado de primera venta.
    return setupTourMatchesRoute(definition, location.pathname) ? definition : null;
  }, [setupTourState, location.pathname]);

  const isSuperAdmin = user?.email === 'adpenia@gmail.com';
  const isAdmin = ['admin', 'administrador'].includes(String(profile?.role || '').toLowerCase());
  const [onboardingGate, setOnboardingGate] = useState(() => ({
    checking: Boolean(profile?.organization_id),
    redirecting: false,
  }));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncFirstSaleOnboarding = () => {
      setFirstSaleOnboardingState(readFirstSaleOnboarding());
    };

    // El estado del recorrido vive en la clave del motor único de onboarding
    // (la misma que usan los tours de puesta a punto y los de módulo).
    const handleStorage = (event) => {
      if (event.key && event.key !== FIRST_SALE_ONBOARDING_STORAGE_KEY) return;
      syncFirstSaleOnboarding();
    };

    syncFirstSaleOnboarding();
    window.addEventListener(FIRST_SALE_ONBOARDING_EVENT, syncFirstSaleOnboarding);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(FIRST_SALE_ONBOARDING_EVENT, syncFirstSaleOnboarding);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    const handleModuleOnboardingCompleteEvent = async (e) => {
      const { moduleId } = e.detail || {};
      if (!moduleId || !profile?.organization_id) return;

      if (getModuleOnboardingStatus(moduleOnboardingStatuses, moduleId) === MODULE_ONBOARDING_STATUS_COMPLETED) {
        return;
      }

      try {
        const nextStatuses = await completeModuleOnboardingStep({
          organizationId: profile.organization_id,
          moduleId,
          currentStatuses: moduleOnboardingStatuses,
        });
        setModuleOnboardingStatuses(nextStatuses);
        setDismissedModuleOnboarding(moduleId);
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(`impulza:force-onboarding:${moduleId}`);
        }
      } catch (error) {
        console.error(`Error completing onboarding for module ${moduleId}:`, error);
      }
    };

    window.addEventListener('impulza:module-onboarding-complete', handleModuleOnboardingCompleteEvent);
    return () => {
      window.removeEventListener('impulza:module-onboarding-complete', handleModuleOnboardingCompleteEvent);
    };
  }, [profile?.organization_id, moduleOnboardingStatuses]);

  const validateOnboardingAccess = useCallback(async ({ blocking = false } = {}) => {
    if (!profile?.organization_id) {
      setOnboardingGate((current) => (
        current.checking === false && current.redirecting === false
          ? current
          : { checking: false, redirecting: false }
      ));
      return false;
    }

    if (blocking) {
      setOnboardingGate((current) => (
        current.checking
          ? current
          : { ...current, checking: true }
      ));
    }

    try {
      const organization = await fetchOrganizationOnboardingStatus(profile.organization_id);
      const shouldRedirect = shouldShowFirstRunOnboarding(organization);

      setOnboardingGate((current) => (
        current.checking === false && current.redirecting === shouldRedirect
          ? current
          : {
            checking: false,
            redirecting: shouldRedirect,
          }
      ));

      if (shouldRedirect) {
        navigate('/sistema/onboarding', { replace: true });
      }

      return shouldRedirect;
    } catch (error) {
      console.error('No pudimos validar el estado del onboarding.', error);
      setOnboardingGate((current) => (
        current.checking === false && current.redirecting === false
          ? current
          : { checking: false, redirecting: false }
      ));
      return false;
    }
  }, [navigate, profile?.organization_id]);

  const handleNavigate = (moduleKey, idOrObj) => {
    const base = '/sistema';

    const resolvedId =
      typeof idOrObj === 'string'
        ? idOrObj
        : (idOrObj && (idOrObj.id || idOrObj.saleId || idOrObj.orderId)) || null;

    // Alias corporativo (sin romper rutas existentes)
    if (moduleKey === 'pedido-web-detalle') {
      if (resolvedId) {
        navigate(`${base}/pedidos-web/${resolvedId}`);
        return;
      }
      navigate(`${base}/pedidos-web`);
      return;
    }

    if (resolvedId) {
      navigate(`${base}/${moduleKey}/${resolvedId}`);
      return;
    }

    navigate(`${base}/${moduleKey}`);
  };

  useEffect(() => {
    validateOnboardingAccess({ blocking: true });
  }, [validateOnboardingAccess]);

  useEffect(() => {
    if (!profile?.organization_id) return undefined;

    const channel = supabase
      .channel(`organization-onboarding-${profile.organization_id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'organizations', filter: `id=eq.${profile.organization_id}` },
        () => {
          validateOnboardingAccess();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.organization_id, validateOnboardingAccess]);

  /* ======================================================
     SITIO SELECCIONADO (compatibilidad)
  ====================================================== */
  useEffect(() => {
    try {
      const raw = localStorage.getItem('sitioSeleccionado') || localStorage.getItem('sitioActivo');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.id || parsed.site_id)) {
        setSitioSeleccionado({ id: parsed.id || parsed.site_id, nombre: parsed.nombre || parsed.name || '' });
      }
    } catch {
      // no rompemos
    }
  }, []);

  /* ======================================================
     PERMISOS Y PLAN DE ORGANIZACION
  ====================================================== */
  const refreshOrgModules = useCallback(async () => {
    if (!profile?.organization_id) return;

    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', profile.organization_id)
      .single();

    if (!orgError && Array.isArray(orgData?.custom_modules)) {
      setOrgModules((current) => (
        areStringArraysEqual(current, orgData.custom_modules) ? current : orgData.custom_modules
      ));
    } else if (!orgError && orgData?.custom_modules === null) {
      setOrgModules((current) => (current === null ? current : null));
    }

    if (!orgError) {
      setOrganizationOnboardingStatus(
        ['pending', 'configured'].includes(String(orgData?.onboarding_status || '').toLowerCase())
          ? 'pending'
          : 'completed',
      );
      setOnboardingV2Enabled(isOnboardingV2Enabled(orgData));
      const nextStatuses = normalizeModuleOnboardingStatuses(orgData?.module_onboarding_statuses);
      setModuleOnboardingStatuses((current) => (
        arePlainObjectsEqual(current, nextStatuses) ? current : nextStatuses
      ));
    }
  }, [profile?.organization_id]);

  useEffect(() => {
    if (!profile?.organization_id) {
      setPermisos([]);
      setLoadingPermisos(false);
      return;
    }

    const fetchDatos = async () => {
      try {
        setLoadingPermisos(true);

        // 1. Obtener módulos habilitados de la organización (Plan)
        // ✅ AQUI BUSCAMOS LA CONFIGURACION DEL SUPERADMIN
        await refreshOrgModules();

        // 2. Obtener permisos efectivos del usuario (plantilla del rol + overrides)
        if (isSuperAdmin) {
          setPermisos(['all']);
          return;
        }

        const { effective } = await fetchEffectiveModulePermissions({
          supabase,
          organizationId: profile.organization_id,
          userId: profile.id,
          roleName: profile.role,
        });

        const effectiveModules = new Set(effective);

        // Un admin puede seguir entrando al panel de administracion de usuarios,
        // aunque el resto de la operatoria quede limitada por sus modulos visibles.
        if (isAdmin) {
          effectiveModules.add('permisos');

          // Fallback de dueño: si la organizacion no tiene `role_templates`
          // sembradas para el rol admin (effective vacio), el admin quedaba
          // viendo TODO como "Sin acceso". En ese caso le damos acceso a todos
          // los modulos habilitados de la organizacion (hasOrgModuleAccess sigue
          // acotando al plan). Los sub-usuarios siguen gobernados por las
          // plantillas; las orgs con plantillas configuradas no se ven afectadas.
          if (effective.size === 0) {
            setPermisos(['all']);
            return;
          }
        }

        setPermisos([...effectiveModules]);
      } catch (e) {
        console.error(e);
        setPermisos([]);
      } finally {
        setLoadingPermisos(false);
      }
    };

    fetchDatos();
  }, [profile, isAdmin, isSuperAdmin, refreshOrgModules]);

  useEffect(() => {
    if (!profile?.organization_id) return;

    const handleOrgModulesUpdated = (event) => {
      const targetOrgId = event?.detail?.organizationId;
      if (!targetOrgId || targetOrgId === profile.organization_id) {
        refreshOrgModules();
      }
    };

    const handleStorage = (event) => {
      if (event.key !== 'impulza:org-modules-updated' || !event.newValue) return;

      try {
        const payload = JSON.parse(event.newValue);
        if (!payload?.organizationId || payload.organizationId === profile.organization_id) {
          refreshOrgModules();
        }
      } catch {
        refreshOrgModules();
      }
    };

    const channel = supabase
      .channel(`organization-modules-${profile.organization_id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'organizations', filter: `id=eq.${profile.organization_id}` },
        () => {
          refreshOrgModules();
        },
      )
      .subscribe();

    window.addEventListener('impulza:org-modules-updated', handleOrgModulesUpdated);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('impulza:org-modules-updated', handleOrgModulesUpdated);
      window.removeEventListener('storage', handleStorage);
      supabase.removeChannel(channel);
    };
  }, [profile?.organization_id, refreshOrgModules]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const container = mainScrollRef.current;
    if (!container) return undefined;

    const storageKey = getRouteScrollStorageKey(location);
    const persistScroll = () => {
      try {
        window.sessionStorage.setItem(storageKey, String(container.scrollTop || 0));
      } catch {
        // Si el navegador bloquea sessionStorage, seguimos igual.
      }
    };

    container.addEventListener('scroll', persistScroll, { passive: true });
    window.addEventListener('beforeunload', persistScroll);
    persistScroll();

    return () => {
      persistScroll();
      container.removeEventListener('scroll', persistScroll);
      window.removeEventListener('beforeunload', persistScroll);
    };
  }, [location.pathname, location.search]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const container = mainScrollRef.current;
    if (!container) return;

    const rawValue = window.sessionStorage.getItem(getRouteScrollStorageKey(location));
    const savedTop = Number(rawValue);
    const nextTop = Number.isFinite(savedTop) && savedTop > 0 ? savedTop : 0;

    const restoreScroll = () => {
      container.scrollTo({ top: nextTop, behavior: 'auto' });
    };

    window.requestAnimationFrame(() => {
      restoreScroll();
      window.requestAnimationFrame(restoreScroll);
    });
  }, [location.pathname, location.search]);

  const permisosSet = useMemo(() => {
    // null = acceso total (sin restricciones)
    // Cuando no hay permisos configurados, el default es acceso a todo.
    // Solo se restringe cuando hay templates explícitos cargados.
    if (!permisos || permisos.length === 0) return null;
    if (permisos.includes('all')) return null;
    return new Set(permisos);
  }, [permisos]);

  const orgModulesSet = useMemo(() => {
    if (orgModules === null) return null;
    return new Set(orgModules);
  }, [orgModules]);

  const hasOrgModuleAccess = (moduleId) => {
    if (!moduleId || moduleId.startsWith('super-admin-')) return true;
    if (['importaciones', 'inventario-ajuste'].includes(moduleId)) return true;
    if (orgModulesSet === null) return true;
    const allowedModuleIds = [moduleId, ...(CRM_ORG_MODULE_ALIAS_MAP[moduleId] || [])];
    return allowedModuleIds.some((candidateId) => orgModulesSet.has(candidateId));
  };

  const hasPermission = (moduleId) => {
    if (moduleId?.startsWith('super-admin-')) return isSuperAdmin;
    if (['importaciones', 'inventario-ajuste'].includes(moduleId)) return true;
    if (!hasOrgModuleAccess(moduleId)) return false;
    if (isSuperAdmin) return true;
    if (permisosSet === null) return true;
    // Los alias heredados del CRM ya vienen resueltos en `permisos`
    // (applyLegacyModuleAliases), respetando el DENY explicito del usuario.
    return permisosSet.has(moduleId);
  };

  const preferredHomeModule = useMemo(() => {
    const preferredOrder = [
      'dashboard',
      'ventas-pos',
      'agenda',
      'clientes',
      'productos',
    ];

    const firstPreferred = preferredOrder.find((moduleId) => hasPermission(moduleId));
    if (firstPreferred) return firstPreferred;

    if (isAdmin && hasPermission('permisos')) return 'permisos';
    if (orgModulesSet === null && isSuperAdmin) return 'dashboard';

    const firstOrgModule = Array.from(orgModulesSet || []).find((moduleId) => hasPermission(moduleId));
    return firstOrgModule || 'permisos';
  }, [hasPermission, isAdmin, isSuperAdmin, orgModulesSet]);

  // `protect` ya no declara el componente: solo lo invoca con lo que necesita.
  // Declararlo adentro creaba un TIPO de componente nuevo en cada render de
  // Sistema, asi que React desmontaba y volvia a montar la pantalla entera con
  // cualquier cambio de estado de este archivo. Con el recorrido guiado eso
  // pasaba en cada paso: el formulario de producto se reiniciaba, los campos
  // quedaban vacios y "Crear producto" enviaba un formulario en blanco.
  const protect = (moduleKey, element) => (
    <ProtectedRoute
      moduleKey={moduleKey}
      hasModuleAccess={hasOrgModuleAccess}
      hasPermission={hasPermission}
      onGoHome={() => navigate(`/sistema/${preferredHomeModule}`)}
    >
      {element}
    </ProtectedRoute>
  );

  const handleSignOut = async () => {
    // Avisos push: este equipo deja de recibir los pedidos de la empresa.
    await desactivarAvisosAdminEsteDispositivo();
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  };

  /* ======================================================
     ACTIVE MODULE (SIDEBAR)
  ====================================================== */
  useEffect(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const idx = parts.indexOf('sistema');
    let module = parts[idx + 1] || 'dashboard';

    const mapToParent = {
      'presupuestos-config': 'presupuestos-config',
      'ventas': 'ventas-historial',
      'ventas-pos': 'ventas-pos',
      'ventas-profesional': 'ventas-profesional',
      'ventas-historial': 'ventas-historial',

      'producto-nuevo': 'productos',
      'producto-editar': 'productos',
      'producto-detalle': 'productos',

      // ✅ Combos cuelgan de Productos (mantiene consistencia visual en Sidebar)
      'combos': 'productos',
      'combo-builder': 'productos',

      'clientes-nuevo': 'clientes',
      'clientes-editar': 'clientes',
      'clientes-detalle': 'clientes',
      'clientes-movimiento': 'clientes',

      // ✅ FIX: Nueva compra debe marcar "compras-nueva", no "compras"
      'compras-nueva': 'compras-nueva',
      'pedido-sugerido': 'pedido-sugerido',
      'compras-ocr': 'compras-ocr', // ✅ Mapeo del nuevo item
      'compras-detalle': 'compras',

      'proveedores-nuevo': 'proveedores',
      'proveedores-editar': 'proveedores',
      'proveedores-detalle': 'proveedores',
      'proveedores-movimiento': 'proveedores',
      'proveedores-form': 'proveedores',

      'bancos-form': 'bancos',
      'bancos-movimiento': 'bancos',
      'gastos-form': 'gastos',
      'caja-detalle': 'caja',
      'caja': 'caja', // agregado explícito

      // ✅ Canal Digital
      'pedidos-web': 'pedidos-web',
      'pedido-web-detalle': 'pedidos-web',
      'notificaciones': 'notificaciones',
      'tienda-config': 'tienda-config',
      'logistica': 'logistica',
      'logistica-entregas': 'logistica-entregas',
      'logistica-operativa': 'logistica-operativa',
      'config-logistica': 'config-logistica',

      // ✅ Costos: mantener seleccionado "Gestion Costos" aunque esté en la pantalla Importar
      'gestion-costos-actualizaciones': 'gestion-costos',
      'gestion-costos-importar': 'gestion-costos',

      // ✅ IA Asistente (módulo propio)
      'chatbot': 'chatbot',

      // ✅ IMPORTACIONES: que se marque correctamente en Sidebar
      'importaciones': 'importaciones',

      /* ======================================================
         ✅ PRODUCCIÓN (AGREGADO)
         Mantener seleccionado el item correcto al navegar
      ====================================================== */
      'produccion-registrar': 'produccion-registrar',
      'produccion-masiva': 'produccion-registrar',
      'pedidos-produccion': 'pedidos-produccion',
      'produccion-flujos': 'produccion-flujos',
      'recetas': 'recetas',
      'produccion-historial': 'produccion-historial',
      'produccion-distribucion': 'produccion-distribucion',

      // ✅ INVENTARIO: Ajuste masivo comparte item de menu con el ajuste simple
      'inventario-ajuste-masivo': 'inventario-ajuste',

      // ✅ INVENTARIO: Módulo de Etiquetas
      'inventario-etiquetas': 'inventario',

      // ✅ FINANZAS: Reportes Impositivos
      'reportes-impositivos': 'reportes-impositivos',
      'reportes-ctas-ctes': 'reportes-ctas-ctes',
      'conciliacion-bancaria': 'conciliacion-bancaria',
      'conciliacion-ctas-ctes': 'conciliacion-ctas-ctes',

      // ✅ CONFIG REPORTES: Hacemos que ilumine la sección de Finanzas o Sistema
      'config-reportes': 'config-finanzas-impuestos',
    };

    module = mapToParent[module] || module;

    setActiveModule(module);

    // Bitacora de adopcion: que modulo abre cada usuario. Es best-effort y
    // deduplicada en sessionAudit (un modulo por ventana), asi que no agrega
    // una escritura por cada ida y vuelta entre pantallas. Esperamos a tener
    // el perfil para que la fila salga con organizacion y rol; el efecto
    // depende de organization_id justamente para registrar la primera
    // navegacion cuando el perfil termina de cargar.
    if (profile?.organization_id) {
      logNavigation(module, { user, profile });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, profile?.organization_id]);

  useEffect(() => {
    setDismissedModuleOnboarding(null);
  }, [activeModule]);

  // Hidratar los cierres persistidos al conocer el usuario.
  useEffect(() => {
    const userId = profile?.id || user?.id;
    if (!userId) return;
    let vivo = true;
    cargarTourDismissals(userId).then((state) => {
      if (vivo) setTourDismissals(state);
    });
    return () => { vivo = false; };
  }, [profile?.id, user?.id]);

  const activeFirstSaleOnboarding = useMemo(() => {
    if (!matchesFirstSaleOnboardingIdentity(firstSaleOnboardingState, {
      organizationId: profile?.organization_id,
      userId: profile?.id || user?.id,
    })) {
      return null;
    }

    return firstSaleOnboardingState;
  }, [firstSaleOnboardingState, profile?.organization_id, profile?.id, user?.id]);

  // El tramo 'conocer-progreso' se cierra cuando el usuario LLEGA a la lista de
  // puesta a punto, no cuando hace clic en un boton del cartel.
  //
  // Antes dependia del listener que el coach-mark engancha sobre el elemento
  // resaltado. Ese efecto hace un unico document.querySelector y no reintenta, y
  // el boton "Empeza a operar" del topbar se monta condicionado a
  // showSetupProgress (= setupChecklist.length > 0), que llega despues de una
  // consulta async: si el paso se activaba antes de que el checklist terminara
  // de cargar, no habia elemento al que escuchar y el recorrido se quedaba
  // trabado ahi para siempre.
  //
  // Detectarlo por ruta ademas cubre los otros caminos a la misma pantalla
  // (el menu, la URL directa, volver atras): llegaste, el tramo se dio.
  useEffect(() => {
    if (activeFirstSaleOnboarding?.stepId !== 'conocer-progreso') return;
    if (!location.pathname.startsWith('/sistema/progreso')) return;
    goToFirstSaleOnboardingStep('elegir-camino');
  }, [activeFirstSaleOnboarding?.stepId, location.pathname]);


  useEffect(() => {
    setDismissedFirstSaleOnboarding(null);
  }, [activeFirstSaleOnboarding?.stepId]);

  // Hook de control de rutas e integración de onboarding (Guard + Detección de navegación)
  useEffect(() => {
    if (!activeFirstSaleOnboarding?.stepId) return;
    if (location.pathname === '/sistema/onboarding') return;

    const path = location.pathname;
    const currentStepId = activeFirstSaleOnboarding.stepId;
    let nextStepId = null;

    // 1. Detectar si el path actual avanza el paso de navegación.
    // Tour corto (producto → venta → rentabilidad → progreso): los avances de
    // create-product → sell-product → review-profitability los emiten las
    // páginas (ProductoNuevo/VentasPOS, viajan desde Impulza) con la demo
    // simulada; acá solo quedan las transiciones de navegación propias.
    if (currentStepId === 'nav-to-productos' && (path.startsWith('/sistema/productos') || path.startsWith('/sistema/productos-form') || path.startsWith('/sistema/producto-nuevo'))) {
      nextStepId = 'create-product';
    } else if (currentStepId === 'review-profitability' && (path === '/sistema' || path === '/sistema/' || path.startsWith('/sistema/dashboard'))) {
      nextStepId = 'review-dashboard';
    } else if (currentStepId === 'nav-to-progreso' && path.startsWith('/sistema/progreso')) {
      nextStepId = 'visit-progreso';
    }

    if (nextStepId) {
      goToFirstSaleOnboardingStep(nextStepId);
      // Evitamos redirigir de inmediato porque ya estamos navegando al path del siguiente paso
      return;
    }

    // 2. Si no es una transición de navegación directa, aplicar el Guard de ruta esperado
    const expectedRoute = getFirstSaleOnboardingRoute(currentStepId);
    if (!expectedRoute) return;

    // Permitir sub-rutas de carga de formularios para los pasos de creación
    if (currentStepId === 'create-product') {
      if (
        path.startsWith('/sistema/productos') ||
        path.startsWith('/sistema/producto-nuevo') ||
        path.startsWith('/sistema/productos-form')
      ) {
        return;
      }
    }
    if (path.startsWith(expectedRoute)) return;

    navigate(expectedRoute, { replace: true });
  }, [activeFirstSaleOnboarding?.stepId, location.pathname, navigate]);

  // 1.4 — Hidratar el recorrido desde onboarding_progress al abrir sesion, para
  // retomar desde otro dispositivo. Best-effort; para usuarios existentes (sin
  // progreso en servidor) no hace nada.
  useEffect(() => {
    const userId = profile?.id || user?.id;
    if (!userId) return;
    hydrateFirstSaleOnboarding({
      organizationId: profile?.organization_id,
      userId,
    });
  }, [profile?.id, profile?.organization_id, user?.id]);

  // H5 — Provisioning reanudable: si el alta del paso 2 quedó a medias (pestaña
  // cerrada en medio del provisioning), completa las piezas base faltantes al
  // entrar. Es un select barato cuando la org está completa; best-effort.
  useEffect(() => {
    if (!profile?.organization_id) return;
    resumePendingProvisioning({ organizationId: profile.organization_id });
  }, [profile?.organization_id]);

  // ✅ RUTAS PÚBLICAS (cliente final)
  // Si entra por /tienda o /rastreo, no debe ver Sidebar/Topbar ni caer en redirecciones del ERP.
  const isPublicStoreRoute =
    location.pathname.startsWith('/tienda') ||
    location.pathname.startsWith('/rastreo');

  if (isPublicStoreRoute) {
    return (
      <Routes>
        <Route path="/tienda/:slug" element={<TiendaWeb />} />
        <Route path="/rastreo/:codigo" element={<Rastreo />} />

        {/* Fallback: evitamos que un 404 lo mande al backoffice */}
        <Route path="*" element={<Navigate to="/tienda/prueba" replace />} />
      </Routes>
    );
  }

  const currentModuleOnboarding = getModuleOnboardingDefinition(activeModule);
  const currentFirstSaleDefinition = activeFirstSaleOnboarding
    ? buildFirstSaleOnboardingDefinition(activeFirstSaleOnboarding.stepId, activeFirstSaleOnboarding)
    : null;
  const firstSaleRoute = activeFirstSaleOnboarding
    ? getFirstSaleOnboardingRoute(activeFirstSaleOnboarding.stepId)
    : '';
  let matchesFirstSaleRoute = false;
  if (activeFirstSaleOnboarding?.stepId && firstSaleRoute) {
    // Los tres pasos del recorrido viven en el dashboard, asi que alcanza con
    // comparar la ruta. El caso especial de 'create-product' murio con el
    // tramo que paseaba por Productos.
    matchesFirstSaleRoute = location.pathname.startsWith(firstSaleRoute);
  }

  const shouldShowFirstSaleOnboarding =
    Boolean(currentFirstSaleDefinition) &&
    matchesFirstSaleRoute &&
    dismissedFirstSaleOnboarding !== activeFirstSaleOnboarding?.stepId;
  const forceOnboardingForModule = activeModule && typeof window !== 'undefined' && window.localStorage.getItem(`impulza:force-onboarding:${activeModule}`) === 'true';

  // F1 — Los tours por modulo estaban muertos para self-serve: exigian
  // organizationOnboardingStatus === 'pending', que el alta ya deja en
  // 'completed'. Ahora, para orgs del onboarding v2, alcanza con que el status
  // por modulo siga pending (se muestran la primera vez que se entra a cada
  // modulo). Se conserva el camino 'pending' para orgs legacy / superadmin, y el
  // force-onboarding (academia "rehacer tour") funciona siempre. Las orgs
  // existentes (no v2, ya completadas) no ven ningun tour por esta via.
  const shouldShowCurrentModuleOnboarding =
    !activeFirstSaleOnboarding &&
    Boolean(currentModuleOnboarding) &&
    (
      forceOnboardingForModule ||
      (
        (onboardingV2Enabled || organizationOnboardingStatus === 'pending') &&
        shouldShowModuleOnboarding(moduleOnboardingStatuses, activeModule) &&
        dismissedModuleOnboarding !== activeModule &&
        // Cierre persistido: si el usuario ya cerro este tour, no vuelve a
        // aparecer aunque recargue o vuelva a loguearse. El "rehacer tour" de
        // la academia sigue funcionando porque entra por forceOnboardingForModule.
        !tourEstaCerrado(tourDismissals, 'modules', activeModule)
      )
    );

  const handleCompleteCurrentModuleOnboarding = async () => {
    if (!profile?.organization_id || !activeModule || moduleOnboardingSaving) return;

    try {
      setModuleOnboardingSaving(true);
      const nextStatuses = await completeModuleOnboardingStep({
        organizationId: profile.organization_id,
        moduleId: activeModule,
        currentStatuses: moduleOnboardingStatuses,
      });
      setModuleOnboardingStatuses(nextStatuses);
      setDismissedModuleOnboarding(activeModule);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(`impulza:force-onboarding:${activeModule}`);
      }
    } catch (error) {
      console.error('No pudimos marcar el onboarding del modulo como visto.', error);
    } finally {
      setModuleOnboardingSaving(false);
    }
  };

  // Terminado el recorrido principal, las guías por módulo dejan de tener
  // sentido: el recorrido ya paseó por dashboard, productos y POS, y a partir
  // de acá lo que se hace lo ELIGE el usuario desde "Empezá a operar".
  //
  // Sin esto, el alta sembraba cinco módulos en `pending` (dashboard,
  // ventas-pos, productos, clientes, inventario) que quedaban suprimidos
  // mientras el recorrido estaba activo y se soltaban todos justo al
  // terminarlo. El efecto para el usuario era el peor posible: cerraba el
  // recorrido para empezar a operar, entraba a Productos a cargar su catálogo
  // real y le saltaba encima la guía de producto. Terminaba una guía y
  // empezaba otra sin haberla pedido.
  //
  // Solo aplica al FIN del recorrido, no a pausarlo: si lo pausa puede
  // retomarlo, y ahí las guías por módulo siguen teniendo sentido.
  const cerrarGuiasDeModuloTrasElRecorrido = () => {
    if (!profile?.organization_id) return;

    const actuales = normalizeModuleOnboardingStatuses(moduleOnboardingStatuses);
    const pendientes = Object.keys(actuales).filter(
      (moduleId) => actuales[moduleId] === MODULE_ONBOARDING_STATUS_PENDING,
    );
    if (pendientes.length === 0) return;

    const siguientes = { ...actuales };
    pendientes.forEach((moduleId) => {
      siguientes[moduleId] = MODULE_ONBOARDING_STATUS_COMPLETED;
    });

    // Se aplica en local primero: si esperáramos a la base, el cartel de la
    // guía de módulo alcanza a aparecer durante el viaje de ida y vuelta.
    setModuleOnboardingStatuses(siguientes);

    updateModuleOnboardingStatuses({
      organizationId: profile.organization_id,
      moduleOnboardingStatuses: siguientes,
    }).catch((error) => {
      console.error('[Sistema] No se pudieron cerrar las guias por modulo tras el recorrido:', error);
    });
  };

  // Cierre del recorrido principal. Termine como termine (último paso o
  // salteando lo que queda), el usuario aterriza en "Empezá a operar" para
  // elegir qué guía hacer y seguir desde ahí.
  const finishFirstSaleOnboarding = (stepId) => {
    completeFirstSaleOnboarding();
    setDismissedFirstSaleOnboarding(stepId || null);
    cerrarGuiasDeModuloTrasElRecorrido();
    navigate('/sistema/progreso');
    toast.success('¡Recorrido guiado completado! 🎉 Elegí la próxima guía para seguir configurando tu negocio.', {
      duration: 8000,
      position: 'top-center',
    });
  };

  const handleCompleteFirstSalePrompt = async () => {
    if (!activeFirstSaleOnboarding?.stepId) return;

    if (activeFirstSaleOnboarding.stepId === 'welcome-demo') {
      goToFirstSaleOnboardingStep('conocer-academia');
      return;
    }

    if (activeFirstSaleOnboarding.stepId === 'conocer-academia') {
      goToFirstSaleOnboardingStep('conocer-progreso');
      return;
    }

    // 'conocer-progreso' se cierra con el clic en "Empezá a operar", que ya
    // navega por su cuenta. Igual navegamos acá porque este handler tambien
    // corre por la red de seguridad del cartel (el boton manual que aparece a
    // los 12s si el auto-avance no llego): sin esto el recorrido avanzaba al
    // tramo siguiente y el usuario se quedaba en el dashboard mirando un
    // cartel que le hablaba de una pantalla que nunca se abrio.
    if (activeFirstSaleOnboarding.stepId === 'conocer-progreso') {
      goToFirstSaleOnboardingStep('elegir-camino');
      navigate('/sistema/progreso');
      return;
    }

    if (activeFirstSaleOnboarding.stepId === 'elegir-camino') {
      finishFirstSaleOnboarding('elegir-camino');
      return;
    }

    setDismissedFirstSaleOnboarding(activeFirstSaleOnboarding.stepId);
  };

  // 1.3 — Saltear el paso actual del recorrido: avanza al siguiente (o finaliza).
  const handleSkipFirstSaleStep = () => {
    const stepId = activeFirstSaleOnboarding?.stepId;
    if (!stepId) return;
    const idx = FIRST_SALE_ONBOARDING_STEPS.indexOf(stepId);
    const nextStep = idx >= 0 ? FIRST_SALE_ONBOARDING_STEPS[idx + 1] : null;
    if (nextStep) {
      goToFirstSaleOnboardingStep(nextStep);
      const route = getFirstSaleOnboardingRoute(nextStep);
      if (route) navigate(route, { replace: true });
    } else {
      finishFirstSaleOnboarding(stepId);
    }
  };

  // 1.3 — Salir del recorrido: lo pausa (persiste) para poder retomarlo luego.
  const handleExitFirstSaleTour = () => {
    pauseFirstSaleOnboarding();
    setDismissedFirstSaleOnboarding(activeFirstSaleOnboarding?.stepId || null);
  };

  if (onboardingGate.checking || onboardingGate.redirecting || loadingPermisos) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#FBF8F1]">
        <div className="text-center">
          <Loader2 className="mx-auto animate-spin text-[var(--text-muted)]" size={32} />
          <p className="mt-4 text-[11px] font-black uppercase tracking-[0.24em] text-[var(--text-muted)]">
            {onboardingGate.redirecting ? 'Abriendo onboarding' : 'Preparando sistema'}
          </p>
        </div>
      </div>
    );
  }

  if (user && !profile?.organization_id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF8F1] px-6">
        <div className="w-full max-w-lg rounded-[2rem] border border-[color:var(--border)] bg-[var(--surface)] p-8 shadow-sm text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.24em] text-amber-500">Perfil incompleto</p>
          <h1 className="mt-3 text-2xl font-black text-[var(--text-default)]">No encontramos una empresa vinculada a tu usuario</h1>
          <p className="mt-3 text-sm font-medium text-[var(--text-muted)]">
            El registro se autentico, pero tu perfil todavia no quedo asociado a una organizacion. Cierra sesion y vuelve a completar el alta para terminar la configuracion.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={() => navigate('/registro-onboarding', { replace: true })}
              className="rounded-md px-5 py-3 text-[11px] font-black uppercase tracking-widest text-[var(--color-accent-dark)] transition-all"
              style={{ background: 'var(--gst-mint-500)' }}
            >
              Reintentar alta
            </button>
            <button
              type="button"
              onClick={handleSignOut}
              className="rounded-md border px-5 py-3 text-[11px] font-black uppercase tracking-widest transition-all"
              style={{ borderColor: 'var(--gst-border)', color: 'var(--gst-graphite-700)' }}
            >
              Cerrar sesion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--gst-cream-50)' }}>
      <TopbarMenu
        activeModule={activeModule}
        setActiveModule={setActiveModule}
        onNavigate={(m) => navigate(`/sistema/${m}`)}
        permisos={permisos}
        userEmail={user?.email}
        orgModules={orgModules}
        setupProgressPct={setupProgress.pct}
        showSetupProgress={showSetupProgress}
        setupProgressDone={setupProgressDone}
      />

      {/* Subscription / read-only banner */}
      <ReadOnlyBanner />

      <div className="flex-1 flex flex-col">
        <main ref={mainScrollRef} className="flex-1 overflow-y-auto">
          <Routes>

            {/* =====================
               REDIRECTS LEGACY
            ===================== */}
            <Route
              path="configuracion/ventas/cobros/nuevo"
              element={<Navigate to="/sistema/metodos-cobro-form" replace />}
            />
            <Route
              path="configuracion/ventas/cobros/editar/:id"
              element={<Navigate to="/sistema/metodos-cobro-form/:id" replace />}
            />

            {/* =====================
               DEFAULT
            ===================== */}
            <Route path="/" element={<Navigate to={preferredHomeModule} replace />} />

            {/* =====================
               ONBOARDING PROGRESS ✅
            ===================== */}
            <Route path="progreso" element={<ProgresoOnboarding />} />

            {/* =====================
               DASHBOARD
            ===================== */}
            <Route path="dashboard" element={protect('dashboard', <Dashboard />)} />

            {/* =====================
               VENTAS
            ===================== */}
            <Route path="ventas-pos" element={protect('ventas-pos', <VentasPOS onNavigate={handleNavigate} />)} />
            <Route path="ventas-profesional" element={protect('ventas-profesional', <VentasProfesional onNavigate={handleNavigate} />)} />
            <Route path="ventas-historial" element={protect('ventas-historial', <VentasHistorial onNavigate={handleNavigate} />)} />
            <Route path="ventas/detalle/:id" element={protect('ventas-historial', <VentaDetalle onNavigate={handleNavigate} />)} />
            <Route path="facturacion" element={protect('facturacion', <Facturacion onNavigate={handleNavigate} />)} />
            <Route path="presupuestos" element={protect('presupuestos', <Presupuestos />)} />

            {/* <Route
              path="listas-precios"
              element={<ModuloPlaceholder title="Listas de precios" actionLabel="Nueva lista" itemsLabel="Listas" />}
            /> 
            */}
            {/* ✅ RUTA REAL DE LISTAS DE PRECIOS */}
            <Route path="listas-precios" element={protect('listas-precios', <ListasPrecios />)} />
            {/* Rutas para formulario */}
            <Route path="listas-precios/nueva" element={protect('listas-precios', <ListasPreciosForm />)} />
            <Route path="listas-precios/editar/:id" element={protect('listas-precios', <ListasPreciosForm />)} />

            <Route path="tipos-comprobante" element={protect('tipos-comprobante', <TiposComprobante onNavigate={handleNavigate} />)} />

            {/* =====================
               CANAL DIGITAL ✅
            ===================== */}
            <Route path="tienda-config" element={protect('tienda-config', <TiendaConfig onNavigate={handleNavigate} />)} />
            <Route path="mercado-libre" element={protect('mercado-libre', <IntegracionMercadoLibre />)} />
            <Route path="rappi" element={protect('rappi', <IntegracionRappi />)} />
            <Route path="pedidos-web" element={protect('pedidos-web', <PedidosWeb onNavigate={handleNavigate} />)} />
            <Route path="logistica" element={protect('logistica', <Logistica onNavigate={handleNavigate} />)} />
            <Route path="logistica-entregas" element={protect('logistica', <LogisticaEntregas />)} />
            <Route path="logistica-operativa" element={protect('logistica', <LogisticaOperativa />)} />
            <Route path="devoluciones" element={protect('devoluciones', <Devoluciones onNavigate={handleNavigate} />)} />
            <Route path="devoluciones/nueva" element={protect('devoluciones', <DevolucionNueva onNavigate={handleNavigate} />)} />
            <Route path="devoluciones/:id" element={protect('devoluciones', <DevolucionDetalle onNavigate={handleNavigate} />)} />
            <Route path="inventario-movimientos" element={protect('inventario-movimientos', <InventarioMovimientos onNavigate={handleNavigate} />)} />
            <Route path="reportes" element={protect('reportes', <Reportes />)} />
            <Route path="reportes-inteligentes" element={protect('reportes-inteligentes', <ReportesInteligentes onNavigate={handleNavigate} />)} />


            {/* ✅ FIX: AGREGADA RUTA FALTANTE PARA NAVEGACION CORRECTA */}
            <Route path="pedidos-web/:id" element={protect('pedidos-web', <PedidoDetalle />)} />
            <Route path="pedido-web-detalle/:id" element={protect('pedidos-web', <PedidoDetalle />)} />

            <Route path="notificaciones" element={protect('pedidos-web', <Notificaciones onNavigate={handleNavigate} />)} />

            {/* =====================
               CLIENTES ✅
            ===================== */}
            <Route path="clientes" element={protect('clientes', <Clientes onNavigate={handleNavigate} />)} />
            <Route path="clientes-nuevo" element={protect('clientes', <ClientesForm onNavigate={handleNavigate} />)} />
            <Route path="clientes-editar/:id" element={protect('clientes', <ClientesForm onNavigate={handleNavigate} />)} />
            <Route path="clientes-detalle/:id" element={protect('clientes', <ClientesDetalle onNavigate={handleNavigate} />)} />
            <Route path="clientes-movimiento/:id" element={protect('clientes', <ClientesMovimiento />)} />

            {/* =====================
               PRODUCTOS ✅ (CORREGIDO)
            ===================== */}
            <Route path="productos" element={protect('productos', <Productos onNavigate={handleNavigate} />)} />
            <Route path="producto-nuevo" element={protect('productos', <ProductoNuevo onNavigate={handleNavigate} />)} />
            <Route path="producto-editar/:id" element={protect('productos', <ProductoNuevo onNavigate={handleNavigate} />)} />
            <Route path="producto-detalle/:id" element={protect('productos', <ProductoDetalle onNavigate={handleNavigate} />)} />

            {/* ✅ COMBOS (CUELGAN DE PRODUCTOS) */}
            <Route path="combos" element={protect('productos', <Combos onNavigate={handleNavigate} />)} />
            <Route path="combo-builder" element={protect('productos', <ComboBuilder onNavigate={handleNavigate} />)} />
            <Route path="combo-builder/:id" element={protect('productos', <ComboBuilder onNavigate={handleNavigate} />)} />

            {/* COSTOS */}
            <Route
              path="gestion-costos"
              element={protect('gestion-costos', <GestionCostos onNavigate={handleNavigate} sitioId={sitioSeleccionado?.id || null} />)}
            />

            <Route
              path="gestion-costos-actualizaciones"
              element={protect('gestion-costos', <GestionCostosActualizaciones onNavigate={handleNavigate} sitioId={sitioSeleccionado?.id || null} />)}
            />

            {/* COSTOS - IMPORTAR */}
            <Route
              path="gestion-costos-importar"
              element={protect('gestion-costos', <GestionCostosImportar onNavigate={handleNavigate} sitioId={sitioSeleccionado?.id || null} />)}
            />

            <Route path="inventario" element={protect('inventario', <Inventario onNavigate={handleNavigate} />)} />
            <Route path="inventario-ajuste" element={protect('inventario-ajuste', <InventarioAjuste onNavigate={handleNavigate} />)} />
            <Route path="inventario-ajuste-masivo" element={protect('inventario-ajuste', <InventarioAjusteMasivo onNavigate={handleNavigate} />)} />
            <Route path="inventario-transferencia" element={protect('inventario-transferencia', <InventarioTransferencia onNavigate={handleNavigate} />)} />
            <Route path="inventario-etiquetas" element={protect('inventario-etiquetas', <InventarioEtiquetas onNavigate={handleNavigate} />)} />

            {/* ======================================================
               ✅ PRODUCCIÓN (CORREGIDO EL CIERRE DE COMENTARIO)
               Rutas para: registrar, recetas, historial, distribución
            ====================================================== */}
            <Route
              path="produccion-registrar"
              element={protect('produccion-registrar', <ProduccionRegistrar
                onNavigate={handleNavigate}
                sitioId={sitioSeleccionado?.id || null}
              />)}
            />
            <Route
              path="produccion-masiva"
              element={protect('produccion-registrar', <ProduccionMasiva
                onNavigate={handleNavigate}
                sitioId={sitioSeleccionado?.id || null}
              />)}
            />
            <Route path="pedidos-produccion" element={protect('pedidos-produccion', <PedidosProduccion />)} />
            <Route path="produccion-detalle" element={protect('pedidos-produccion', <ProduccionDetalle />)} />
            <Route path="produccion-flujos" element={protect('pedidos-produccion', <ProduccionFlujos />)} />
            <Route path="recetas" element={protect('recetas', <Recetas onNavigate={handleNavigate} />)} />
            <Route
              path="produccion-historial"
              element={protect('produccion-historial', <ProduccionHistorial onNavigate={handleNavigate} sitioId={sitioSeleccionado?.id || null} />)}
            />
            <Route
              path="produccion-historial/:id"
              element={protect('produccion-historial', <ProduccionHistorialDetalle onNavigate={handleNavigate} />)}
            />
            <Route
              path="produccion-distribucion"
              element={protect('produccion-distribucion', <ProduccionDistribucion onNavigate={handleNavigate} sitioId={sitioSeleccionado?.id || null} />)}
            />

            {/* =====================
               COMPRAS / PROVEEDORES
            ===================== */}
            <Route path="compras" element={protect('compras', <Compras onNavigate={handleNavigate} />)} />
            <Route path="compras-nueva" element={protect('compras-nueva', <ComprasNueva onNavigate={handleNavigate} />)} />
            <Route path="pedido-sugerido" element={protect('pedido-sugerido', <PedidoSugerido onNavigate={handleNavigate} />)} />
            <Route path="compras-detalle/:id" element={protect('compras', <ComprasDetalle onNavigate={handleNavigate} />)} />

            {/* =====================
   PROSPECTOS / LEADS
 ===================== */}
            <Route path="prospectos" element={<Prospectos />} />

            {/* ✅ NUEVA RUTA OCR */}
            <Route path="compras-ocr" element={protect('compras-ocr', <ComprasOCR onNavigate={handleNavigate} />)} />

            <Route path="proveedores" element={protect('proveedores', <Proveedores onNavigate={handleNavigate} />)} />
            <Route path="proveedores-nuevo" element={protect('proveedores', <ProveedoresForm onNavigate={handleNavigate} />)} />
            <Route path="proveedores-form" element={protect('proveedores', <ProveedoresForm onNavigate={handleNavigate} />)} />
            <Route path="proveedores-editar/:id" element={protect('proveedores', <ProveedoresForm onNavigate={handleNavigate} />)} />
            <Route path="proveedores-form/:id" element={protect('proveedores', <ProveedoresForm onNavigate={handleNavigate} />)} />
            <Route path="proveedores-detalle/:id" element={protect('proveedores', <ProveedoresDetalle onNavigate={handleNavigate} />)} />
            <Route path="proveedores-movimiento/:id" element={protect('proveedores', <ProveedoresMovimiento onNavigate={handleNavigate} />)} />

            {/* =====================
               TESORERÍA / CAJA
            ===================== */}
            <Route path="caja" element={protect('caja', <Caja onNavigate={handleNavigate} />)} />
            <Route path="caja-detalle/:id" element={protect('caja', <CajaDetalle onNavigate={handleNavigate} />)} />

            {/* ✅ RUTA AGREGADA (FIX 404): Para soportar la navegación /sistema/caja/detalle/:id */}
            <Route path="caja/detalle/:id" element={protect('caja', <CajaDetalle onNavigate={handleNavigate} />)} />

            <Route path="bancos" element={protect('bancos', <Bancos onNavigate={handleNavigate} />)} />
            <Route path="bancos-form" element={protect('bancos', <BancosForm onNavigate={handleNavigate} />)} />
            <Route path="bancos-form/:id" element={protect('bancos', <BancosForm onNavigate={handleNavigate} />)} />
            <Route path="bancos-movimiento/:id" element={protect('bancos', <BancosMovimiento onNavigate={handleNavigate} />)} />
            <Route path="producto-nuevo/:id?" element={protect('productos', <ProductoNuevo />)} />

            <Route path="gastos" element={protect('gastos', <Gastos onNavigate={handleNavigate} />)} />
            <Route path="gastos-form" element={protect('gastos', <GastosForm onNavigate={handleNavigate} />)} />

            <Route
              path="tesoreria-cobranzas"
              element={protect('tesoreria-cobranzas', <TesoreriaCobranzas onNavigate={handleNavigate} />)}
            />
            <Route
              path="tesoreria-pagos"
              element={protect('tesoreria-pagos', <TesoreriaPagos onNavigate={handleNavigate} />)}
            />

            <Route
              path="tesoreria-transferencia"
              element={protect('tesoreria-transferencia', <TesoreriaTransferencia />)}
            />

            <Route
              path="gestion-costos-reglas"
              element={<ModuloPlaceholder title="Gestión de costos" actionLabel="Nueva regla" itemsLabel="Reglas" />}
            />

            {/* ✅ Rentabilidad real */}
            <Route
              path="rentabilidad-ventas"
              element={protect('rentabilidad-ventas', <RentabilidadVentas onNavigate={handleNavigate} />)}
            />

            <Route
              path="rentabilidad-ventas-placeholder"
              element={<ModuloPlaceholder title="Rentabilidad" actionLabel="Nuevo análisis" itemsLabel="Reportes" />}
            />

            {/* ✅ Reportes Financieros Nuevos */}
            <Route path="reportes-ctas-ctes" element={protect('reportes-ctas-ctes', <ReportesCuentasCorrientes />)} />
            <Route path="conciliacion-bancaria" element={protect('conciliacion-bancaria', <ConciliacionBancaria />)} />
            <Route path="conciliacion-ctas-ctes" element={protect('conciliacion-ctas-ctes', <ConciliacionCuentasCorrientes />)} />

            {/* ✅ Reportes Impositivos */}
            <Route
              path="reportes-impositivos"
              element={protect('reportes-impositivos', <ReportesImpositivos />)}
            />

            {/* =====================
               CONFIGURACIÓN
            ===================== */}
            <Route path="empresa-datos" element={protect('empresa-datos', <EmpresaDatos onNavigate={handleNavigate} />)} />
            <Route path="sitios" element={protect('sitios', <Sitios onNavigate={handleNavigate} />)} />
            <Route path="categorias" element={protect('categorias', <Categorias onNavigate={handleNavigate} />)} />
            <Route path="unidades-medida" element={protect('unidades-medida', <UnidadesMedida onNavigate={handleNavigate} />)} />
            <Route path="config-centros-costo" element={protect('config-sistema-datos', <CentrosCosto onNavigate={handleNavigate} />)} />
            <Route path="config-facturacion" element={protect('config-facturacion', <ConfigFacturacion onNavigate={handleNavigate} />)} />
            <Route path="config-logistica" element={protect('config-logistica', <ConfigLogistica onNavigate={handleNavigate} />)} />

            {/* ✅ NUEVO: Configuración de Reportes (Tickets) */}
            <Route path="config-reportes" element={protect('config-reportes', <ConfigReportes />)} />

            <Route path="presupuestos-config" element={protect('presupuestos-config', <PresupuestosConfig onNavigate={handleNavigate} />)} />

            {/* ✅ RUTAS DE CONFIGURACIÓN NUEVAS (Apuntan a los componentes reales) */}
            <Route
              path="config-productos-stock"
              element={protect('config-sistema-datos', <ConfigProductosStock />)}
            />
            <Route
              path="config-talles-colores"
              element={protect('config-sistema-datos', <ConfigTallesColores />)}
            />
            <Route
              path="config-ventas-cobros"
              element={protect('config-sistema-datos', <ConfigVentas />)}
            />
            <Route
              path="config-finanzas-impuestos"
              element={protect('config-sistema-datos', <ConfigFinanzas />)}
            />
            <Route
              path="config-sistema-datos"
              element={protect('config-sistema-datos', <ConfigSistema />)}
            />

            {/* ✅ RUTAS DE SOPORTE PARA LOS NUEVOS PANELES (Mapean a módulos o placeholders) */}

            {/* Ventas y Cobros */}
            <Route path="configuracion/ventas/predeterminados" element={<ModuloPlaceholder title="Valores Predeterminados" actionLabel="Guardar" itemsLabel="Valores" />} />
            <Route path="configuracion/ventas/cobros" element={<Navigate to="/sistema/metodos-cobro" replace />} />
            <Route path="configuracion/ventas/condiciones" element={<Navigate to="/sistema/condiciones-pago" replace />} />
            <Route path="configuracion/ventas/tienda" element={<Navigate to="/sistema/tienda-config" replace />} />

            {/* Finanzas */}
            <Route path="config-finanzas-impuestos/costos" element={<Navigate to="/sistema/gestion-costos" replace />} />

            {/* ✅ ACTUALIZADO: Apuntamos el link de ConfigFinanzas a la nueva pantalla */}
            {/* <Route path="config-finanzas-impuestos/reportes-impresos" element={<ModuloPlaceholder title="Reportes Impresos" actionLabel="Editar Plantilla" itemsLabel="Plantillas" />} /> */}
            <Route path="config-finanzas-impuestos/reportes-impresos" element={protect('config-reportes', <ConfigReportes />)} />

            {/* Sistema */}
            <Route path="config-sistema-importar" element={<Navigate to="/sistema/importaciones" replace />} />


            {/* ✅ IMPORTACIONES (ruta formal dentro de Sistema) */}
            <Route path="importaciones" element={protect('importaciones', <Importaciones />)} />

            {/* =====================
               VENTAS Y COBROS
            ===================== */}
            <Route path="metodos-cobro" element={protect('metodos-cobro', <MetodosCobro onNavigate={handleNavigate} />)} />
            <Route path="metodos-cobro-form" element={protect('metodos-cobro', <MetodosCobroForm onNavigate={handleNavigate} />)} />
            <Route path="metodos-cobro-form/:id" element={protect('metodos-cobro', <MetodosCobroForm onNavigate={handleNavigate} />)} />
            <Route path="condiciones-pago" element={protect('condiciones-pago', <CondicionesPago onNavigate={handleNavigate} />)} />

            {/* =====================
               FLUJO DE FONDOS
            ===================== */}
            <Route path="flujo-fondos" element={protect('flujo-fondos', <FlujoFondos initialSection="calendario" onNavigate={handleNavigate} />)} />
            <Route path="flujo-fondos/calendario" element={protect('flujo-fondos', <FlujoFondosCalendarioPage />)} />
            <Route path="flujo-fondos/analisis" element={protect('flujo-fondos', <FlujoFondosAnalisisPage />)} />
            <Route path="flujo-fondos/historial" element={protect('flujo-fondos', <FlujoFondosHistorialPage />)} />
            <Route path="flujo-fondos/bancos" element={protect('flujo-fondos', <FlujoFondosBancosPage />)} />
            <Route path="flujo-fondos/:anio/:mes" element={protect('flujo-fondos', <FlujoFondosMesDetalle onNavigate={handleNavigate} />)} />

            {/* =====================
               SEGURIDAD
            ===================== */}
            <Route path="permisos" element={protect('permisos', <Permisos onNavigate={handleNavigate} />)} />
            <Route path="usuario-nuevo" element={protect('permisos', <UsuarioNuevo onNavigate={handleNavigate} />)} />
            <Route path="usuario-detalle/:id" element={protect('permisos', <UsuarioDetalle onNavigate={handleNavigate} />)} />
            <Route path="roles-config" element={protect('permisos', <RolesConfig onNavigate={handleNavigate} />)} />

            {/* =====================
               OTROS
            ===================== */}
            <Route path="agenda" element={protect('agenda', <Agenda onNavigate={handleNavigate} />)} />
            <Route path="agenda/:id/editar" element={protect('agenda', <AgendaEditar />)} />
            <Route path="agenda/:id" element={protect('agenda', <AgendaDetalle />)} />
            <Route path="agenda/nueva" element={protect('agenda', <AgendaNueva />)} />
            <Route path="logs" element={protect('logs', <Logs onNavigate={handleNavigate} />)} />

            {/* ✅ IA ASISTENTE: ahora embebido (NO placeholder) */}
            <Route
              path="chatbot"
              element={protect('chatbot', <Chatbot mode="embedded" />)}
            />

            <Route
              path="super-admin-stats"
              element={<ModuloPlaceholder title="Métricas Globales" actionLabel="Nuevo reporte" itemsLabel="Métricas" />}
            />
            <Route path="super-admin-empresas" element={protect('super-admin-empresas', <SuperAdminEmpresas />)} />
            <Route path="super-admin-pricing" element={protect('super-admin-pricing', <PricingConfig />)} />
            <Route path="super-admin-suscripciones" element={protect('super-admin-suscripciones', <SubscriptionsDashboard />)} />
            {/* Ruta /super-admin-comprobantes eliminada — sin transferencias manuales. */}
            <Route path="super-admin-demo-requests" element={protect('super-admin-demo-requests', <DemoRequests />)} />
            <Route path="super-admin-presets" element={protect('super-admin-empresas', <SuperAdminPresets />)} />
            <Route path="super-admin-sentry" element={protect('super-admin-sentry', <SentryDashboard />)} />
            <Route path="super-admin-adopcion" element={protect('super-admin-adopcion', <Adopcion />)} />
            <Route path="super-admin-auditoria" element={protect('super-admin-auditoria', <AuditoriaSesiones />)} />

            {/* =====================
               ACADEMIA
            ===================== */}
            <Route path="academia" element={<AcademiaImpulza orgModules={orgModules} />} />
            <Route path="academia/:slug" element={<AcademiaViewer orgModules={orgModules} />} />

            {/* =====================
               404
            ===================== */}
            <Route
              path="*"
              element={
                <div className="flex flex-col items-center justify-center h-full p-10">
                  <h2 className="text-2xl font-bold">Módulo no encontrado</h2>
                  <button
                    onClick={() => navigate('/sistema/dashboard')}
                    className="mt-6 bg-mint-500 text-white px-6 py-3 rounded-xl"
                  >
                    Volver al inicio
                  </button>
                </div>
              }
            />
          </Routes>
        </main>
        {/* gst-onboarding-theme: scope local que le da al overlay del onboarding
            una superficie oscura + acento mint fuerte (estilo Impulza), sin
            alterar el chrome claro del resto de Gastify. Ver index.css. */}
        {activeSetupTourDefinition ? (
          <div className="gst-onboarding-theme">
            <ModuleOnboardingPrompt
              moduleDefinition={activeSetupTourDefinition}
              onDismiss={cancelSetupTour}
              onComplete={handleSetupTourCompleted}
              onSkipStep={cancelSetupTour}
              onExit={cancelSetupTour}
              completeLabel="Finalizar guía"
              dismissLabel="Ocultar"
              introCompleteLabel="Comenzar guía"
            />
          </div>
        ) : shouldShowFirstSaleOnboarding ? (
          <div className="gst-onboarding-theme">
            <ModuleOnboardingPrompt
              moduleDefinition={currentFirstSaleDefinition}
              onDismiss={handleExitFirstSaleTour}
              onComplete={handleCompleteFirstSalePrompt}
              onSkipStep={handleSkipFirstSaleStep}
              onExit={handleExitFirstSaleTour}
              dismissLabel="Pausar guía"
              completeLabel={
                activeFirstSaleOnboarding?.stepId === 'welcome-demo'
                  ? 'Seguir'
                  : activeFirstSaleOnboarding?.stepId === 'conocer-academia'
                  ? 'Entendido'
                  : activeFirstSaleOnboarding?.stepId === 'elegir-camino'
                  ? 'Listo, gracias'
                  : 'Entendido'
              }
              introCompleteLabel={activeFirstSaleOnboarding?.stepId === 'welcome-demo' ? 'Comenzar recorrido' : 'Entendido'}
            />
          </div>
        ) : shouldShowCurrentModuleOnboarding ? (
          <div className="gst-onboarding-theme">
            <ModuleOnboardingPrompt
              moduleDefinition={currentModuleOnboarding}
              saving={moduleOnboardingSaving}
              onDismiss={() => {
                setDismissedModuleOnboarding(activeModule);
                if (typeof window !== 'undefined') {
                  window.localStorage.removeItem(`impulza:force-onboarding:${activeModule}`);
                }
                // Persistimos el cierre para que no reaparezca en el proximo login.
                marcarTourCerrado({
                  userId: profile?.id || user?.id,
                  organizationId: profile?.organization_id,
                  tipo: 'modules',
                  id: activeModule,
                  estadoActual: tourDismissals,
                }).then(setTourDismissals);
              }}
              onComplete={handleCompleteCurrentModuleOnboarding}
            />
          </div>
        ) : null}

        {/* ❌ ELIMINADO: Chatbot flotante global */}
      </div>

    </div>
  );
};

export default Sistema;
